import _ from "lodash";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { useI18n } from "Hooks";
import { FilterOutlined } from "@toddle-edu/ds-icons";
import { FilterMenu, IconDisclosure } from "@toddle-edu/ds-web";
import { CURRICULUM_TYPE_MYP } from "Constants/stringConstants";
import {
  getMypPlannerElementSetsQueryEnhancer,
  getMypCriterionQueryEnhancer,
  getMYPObjectivesQueryEnhancer,
} from "ToddleLibrary/modules/ToddleLibraryQueryEnhancers";
import {
  getMypFilterVariables,
  getMypFilterLabelCount,
  getMypFilterArray,
  getWorksheetPlannerElementNodeIntersectionSet,
  getMYPObjectivesVariablesForTopicsAndSubtopics,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getFilterMenuOptions } from "ToddleLibrary/components/MYPFilterMenu/modules/MYPFilterMenuModule";

const WorksheetAdditionalFilter = ({
  mypRelatedConcepts,
  keyConcepts,
  globalContext,
  onChangeFilterValues,
  subjectId,
  criterion,
  topics,
}) => {
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [labelValue, setLabelValue] = useState(0);

  const { t } = useI18n(["common", "library"]);

  useEffect(() => {
    setLabelValue(getMypFilterLabelCount(selectedItemKeys));
  }, [selectedItemKeys]);

  /**
   * Debounced version of onChangeFilterValues function to limit the rate at which the filter
   * is applied when user changes filter values. This improves performance by preventing
   * excessive function calls during rapid input changes .
   */
  const debouncedOnChangeFilterValues = useCallback(
    _.debounce((worksheetFilters, keys) => {
      onChangeFilterValues(worksheetFilters, keys);
    }, 500),
    [onChangeFilterValues]
  );

  // Combine MYP data for filter options
  const relatedConcepts = useMemo(() => {
    return _.reduce(
      mypRelatedConcepts,
      (acc, { id, label, associatedParents }) => {
        if (_.some(associatedParents, parent => parent.id === subjectId)) {
          acc.push({ value: id, label });
        }
        return acc;
      },
      []
    );
  }, [mypRelatedConcepts, subjectId]);

  // Generate filter menu options
  const filterMenuOptions = useMemo(
    () =>
      getFilterMenuOptions({
        relatedConcepts,
        keyConcepts,
        globalContext,
        criterion,
        topics,
        t,
      }),
    [relatedConcepts, keyConcepts, globalContext, criterion, topics, t]
  );

  const handleAdditionalFilterChange = useCallback(
    keys => {
      setSelectedItemKeys(keys || {});

      const filterConfig = [
        { key: "relatedConcepts", options: relatedConcepts },
        { key: "keyConcepts", options: keyConcepts },
        { key: "globalContext", options: globalContext },
        { key: "criterion", options: criterion },
      ];

      const params = _.reduce(
        filterConfig,
        (acc, { key, options }) => {
          acc[key] = getMypFilterArray(keys, key, options);
          return acc;
        },
        {}
      );

      // Extract topics directly from keys (no mutation needed)
      const topicsFromKeys = _.get(keys, "topics", []);

      const worksheetPlannerElementNodeIntersectionSet = getWorksheetPlannerElementNodeIntersectionSet(
        params
      );

      // Build worksheetFilters object conditionally
      const worksheetFilters = {};

      // Add topics only if not empty
      if (!_.isEmpty(topicsFromKeys)) {
        worksheetFilters.mypTopics = topicsFromKeys;
      }

      // Add worksheetPlannerElementNodeIntersectionSet only if not empty
      if (!_.isEmpty(worksheetPlannerElementNodeIntersectionSet)) {
        worksheetFilters.worksheetPlannerElementNodeIntersectionSet = worksheetPlannerElementNodeIntersectionSet;
      }

      debouncedOnChangeFilterValues(
        {
          worksheetFilters,
        },
        Object.keys(params)
      );
    },
    [
      relatedConcepts,
      keyConcepts,
      globalContext,
      criterion,
      debouncedOnChangeFilterValues,
    ]
  );

  return (
    <div>
      <FilterMenu
        options={filterMenuOptions}
        selectedItemKeys={selectedItemKeys}
        isSearchEnabled={true}
        isResetAllEnabled={true}
        isSelectAllEnabled={false}
        showSelectedOptionsOnTop={false}
        isPinEnabled={false}
        noResultDescription={t("common:noResultMsg")}
        noResultLabel={t("common:noResultHeading")}
        onChange={handleAdditionalFilterChange}
        testId="worksheetAdditionalFilter-filterMenu"
      >
        <IconDisclosure
          dsVersion="2.0"
          icon={<FilterOutlined />}
          variant="neutral"
          type="plain"
          badgeProps={{
            value: labelValue,
            variant: "dark",
            shape: "circle",
          }}
          testId="worksheetAdditionalFilter-filterMenu-disclosure"
        />
      </FilterMenu>
    </div>
  );
};

WorksheetAdditionalFilter.propTypes = {
  selectedGradeData: PropTypes.object,
  selectedSubjectData: PropTypes.object,
  mypRelatedConcepts: PropTypes.array,
  keyConcepts: PropTypes.array,
  globalContext: PropTypes.array,
  onChangeFilterValues: PropTypes.func.isRequired,
  curriculumProgramId: PropTypes.string,
  mypRelatedConceptsVariables: PropTypes.object,
  mypKeyConceptsVariables: PropTypes.object,
  mypGlobalContextVariables: PropTypes.object,
  mypObjectivesSubtopicsVariables: PropTypes.object,
  topics: PropTypes.array,
  isData: PropTypes.bool,
  isLoading: PropTypes.bool,
};

WorksheetAdditionalFilter.defaultProps = {
  selectedGradeData: {},
  selectedSubjectData: {},
  mypRelatedConcepts: [],
  keyConcepts: [],
  globalContext: [],
  subjectId: "",
  curriculumProgramId: "",
  mypRelatedConceptsVariables: {},
  mypKeyConceptsVariables: {},
  mypGlobalContextVariables: {},
  mypObjectivesSubtopicsVariables: {},
  topics: [],
  isData: false,
  isLoading: false,
};

const mapStateToProps = (state, ownProps) => {
  const {
    selectedSubjectData: subject,
    selectedGradeData: grade,
    curriculumProgramId,
  } = ownProps;

  let mypFilterVariables = {};
  let subjectId = "";
  let mypObjectivesVariables = {};

  // Get MYP filter variables
  mypFilterVariables = getMypFilterVariables(curriculumProgramId);

  // Get MYP objectives variables for subtopics
  const subjectEntityList = _.get(subject, "mappedOrganizationEntities", []);

  // Filter subjects to only get those with IB_MYP curriculum type
  const mypSubjects = _.filter(
    subjectEntityList,
    subject => _.get(subject, "curriculumProgram.type") === CURRICULUM_TYPE_MYP
  );

  subjectId = _.get(mypSubjects, "[0].id", "");

  // Get grade IDs for MYP objectives
  const gradeIds = _.map(_.get(grade, "mappedOrganizationEntities", []), "id");

  ({ mypObjectivesVariables } = getMYPObjectivesVariablesForTopicsAndSubtopics({
    curriculumProgramId,
    gradeIds,
    subjectIds: [subjectId],
  }));

  const {
    mypKeyConceptsVariables,
    mypGlobalContextVariables,
    mypRelatedConceptsVariables,
  } = mypFilterVariables;

  return {
    curriculumProgramId,
    mypRelatedConceptsVariables,
    mypKeyConceptsVariables,
    mypGlobalContextVariables,
    mypObjectivesVariables,
    subjectId,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  connect(mapStateToProps),
  // Related Concepts
  getMypPlannerElementSetsQueryEnhancer({
    name: "getMYPRelatedConcepts",
    variablesKey: "mypRelatedConceptsVariables",
    dataPropName: "mypRelatedConcepts",
  }),
  // Key Concepts
  getMypPlannerElementSetsQueryEnhancer({
    name: "getMYPKeyConcepts",
    variablesKey: "mypKeyConceptsVariables",
    dataPropName: "keyConcepts",
  }),
  // Global Contexts
  getMypPlannerElementSetsQueryEnhancer({
    name: "getMYPGlobalContext",
    variablesKey: "mypGlobalContextVariables",
    dataPropName: "globalContext",
  }),
  //Criterion
  getMypCriterionQueryEnhancer({
    name: "getMypCriterion",
    variablesKey: "curriculumProgramId",
    dataPropName: "criterion",
  }),
  // For topics (upToDepth: 1, startDepth: 1)
  getMYPObjectivesQueryEnhancer({
    name: "getMYPObjectives",
    variablesKey: "mypObjectivesVariables",
    dataPropName: "topics",
    pickFields: ["id", "label"],
  })
)(WorksheetAdditionalFilter);
